import { useCallback, useEffect, useMemo, useState } from 'react'
import { ClientResponseError } from 'pocketbase'
import {
  COL_CONSUMABLES,
  COL_FINISHED_GOODS_LOTS,
  COL_INVENTORY_MOVEMENTS,
} from '../../config/collections'
import { PERMISSION_KEYS } from '../../config/permissions'
import { useAuth } from '../../context/AuthContext'
import { ExportCsvButton } from '../../components/ExportCsvButton'
import type { CsvColumn } from '../../lib/exportCsv'
import { matchesListSearch } from '../../lib/listSearch'
import { formatDateRu, formatDateTimeRu, formatQty } from '../../lib/scheduleCalculations'
import { unitLabel } from '../../lib/units'
import { pocketbaseErrorMessage } from '../../lib/pocketbaseErrorMessage'
import { consumableSavePatch } from '../../lib/pbRecordDefaults'
import { pb } from '../../pocketbase'
import { Alert, FormField, Modal, ModalActions, PageHeader, PageShell, SearchField, modalBtn } from '../../components/ui'
import { formInputProps } from '../../components/ui/FormField'
import {
  btn,
  cn,
  inputClass,
  tabLink,
  tableCellClass,
  tableClass,
  tableHeadClass,
  tableLastRowClass,
  tableRowHoverClass,
  tableWrapClass,
  tabsWrapClass,
  toolbarClass,
} from '../../lib/ui'
import type { ConsumableRecord, FinishedGoodsLotRecord, InventoryMovementRecord, InventoryMovementType } from '../../types/records'

type TabId = 'stock' | 'movements' | 'alerts' | 'fg_lots'

const MOVEMENT_TYPE_LABELS: Record<InventoryMovementType, string> = {
  receipt: 'Приход',
  issue: 'Списание',
  adjustment: 'Корректировка',
  reserve: 'Резерв',
  unreserve: 'Снятие резерва',
}

const MANUAL_MOVEMENT_TYPES: InventoryMovementType[] = ['receipt', 'issue', 'adjustment']

function stockDelta(type: InventoryMovementType, quantity: number): number {
  switch (type) {
    case 'receipt':
      return Math.abs(quantity)
    case 'issue':
      return -Math.abs(quantity)
    case 'adjustment':
      return quantity
    default:
      return 0
  }
}

function isLowStock(row: ConsumableRecord): boolean {
  const min = row.minStock
  if (min == null || !Number.isFinite(Number(min)) || Number(min) <= 0) return false
  return Number(row.stockQty ?? 0) < Number(min)
}

export function InventoryPage() {
  const { user, can } = useAuth()
  const canWrite = can(PERMISSION_KEYS.INVENTORY_WRITE)

  const [tab, setTab] = useState<TabId>('stock')
  const [consumables, setConsumables] = useState<ConsumableRecord[]>([])
  const [movements, setMovements] = useState<InventoryMovementRecord[]>([])
  const [fgLots, setFgLots] = useState<FinishedGoodsLotRecord[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(true)

  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [movConsumableId, setMovConsumableId] = useState('')
  const [movType, setMovType] = useState<InventoryMovementType>('receipt')
  const [movQuantity, setMovQuantity] = useState('')
  const [movNotes, setMovNotes] = useState('')

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    const errors: string[] = []

    try {
      const cList = await pb.collection(COL_CONSUMABLES).getFullList<ConsumableRecord>({
        sort: 'name',
        expand: 'consumable_category',
      })
      setConsumables(cList)
    } catch (e: unknown) {
      errors.push(`Остатки: ${pocketbaseErrorMessage(e, 'ошибка загрузки')}`)
      setConsumables([])
    }

    try {
      const mList = await pb.collection(COL_INVENTORY_MOVEMENTS).getFullList<InventoryMovementRecord>({
        sort: '-id',
        expand: 'consumable',
      })
      setMovements(mList)
    } catch (e: unknown) {
      let msg = pocketbaseErrorMessage(e, 'ошибка загрузки')
      if (e instanceof ClientResponseError && e.status === 404) {
        msg = 'коллекция inventory_movements не найдена — примените миграции PocketBase (1745190012) и перезапустите сервер'
      }
      errors.push(`Движения: ${msg}`)
      setMovements([])
    }

    try {
      const fgList = await pb.collection(COL_FINISHED_GOODS_LOTS).getFullList<FinishedGoodsLotRecord>({
        sort: '-producedAt',
        expand: 'product',
      })
      setFgLots(fgList)
    } catch (e: unknown) {
      errors.push(`Партии ГП: ${pocketbaseErrorMessage(e, 'ошибка загрузки')}`)
      setFgLots([])
    }

    if (errors.length > 0) {
      setErr(errors.join(' '))
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const alerts = useMemo(() => consumables.filter(isLowStock), [consumables])

  const filteredStock = useMemo(() => {
    if (!searchQuery.trim()) return consumables
    return consumables.filter((row) =>
      matchesListSearch(
        searchQuery,
        row.name,
        row.code,
        row.expand?.consumable_category?.name,
        String(row.stockQty),
        row.minStock != null ? String(row.minStock) : '',
      ),
    )
  }, [consumables, searchQuery])

  const filteredMovements = useMemo(() => {
    if (!searchQuery.trim()) return movements
    return movements.filter((row) =>
      matchesListSearch(
        searchQuery,
        row.expand?.consumable?.name,
        row.expand?.consumable?.code,
        MOVEMENT_TYPE_LABELS[row.type],
        row.notes,
        String(row.quantity),
      ),
    )
  }, [movements, searchQuery])

  const filteredFgLots = useMemo(() => {
    if (!searchQuery.trim()) return fgLots
    return fgLots.filter((row) =>
      matchesListSearch(
        searchQuery,
        row.expand?.product?.name,
        row.expand?.product?.code,
        row.lotCode,
        row.warehouseLocation,
        String(row.quantity),
      ),
    )
  }, [fgLots, searchQuery])

  const stockCsvColumns: CsvColumn<ConsumableRecord>[] = useMemo(
    () => [
      { header: 'Наименование', value: (r) => r.name },
      { header: 'Код', value: (r) => r.code ?? '' },
      { header: 'Ед.', value: (r) => unitLabel(r.unit) },
      { header: 'Остаток', value: (r) => Number(r.stockQty ?? 0) },
      { header: 'Мин. запас', value: (r) => (r.minStock != null ? Number(r.minStock) : '') },
    ],
    [],
  )

  const movementsCsvColumns: CsvColumn<InventoryMovementRecord>[] = useMemo(
    () => [
      { header: 'Дата', value: (r) => formatDateTimeRu(r.created) },
      { header: 'Расходник', value: (r) => r.expand?.consumable?.name ?? '' },
      { header: 'Тип', value: (r) => MOVEMENT_TYPE_LABELS[r.type] ?? r.type },
      { header: 'Кол-во', value: (r) => Number(r.quantity) },
      { header: 'Примечание', value: (r) => r.notes ?? '' },
    ],
    [],
  )

  const fgLotsCsvColumns: CsvColumn<FinishedGoodsLotRecord>[] = useMemo(
    () => [
      { header: 'Продукция', value: (r) => r.expand?.product?.name ?? '' },
      { header: 'Код партии', value: (r) => r.lotCode ?? '' },
      { header: 'Кол-во', value: (r) => Number(r.quantity) },
      { header: 'Дата выпуска', value: (r) => formatDateRu(r.producedAt) },
      { header: 'Ячейка', value: (r) => r.warehouseLocation ?? '' },
    ],
    [],
  )

  const filteredAlerts = useMemo(() => {
    if (!searchQuery.trim()) return alerts
    return alerts.filter((row) =>
      matchesListSearch(searchQuery, row.name, row.code, String(row.stockQty), String(row.minStock)),
    )
  }, [alerts, searchQuery])

  function openMovementModal(consumableId?: string) {
    setMovConsumableId(consumableId ?? '')
    setMovType('receipt')
    setMovQuantity('')
    setMovNotes('')
    setModalOpen(true)
  }

  async function saveMovement() {
    if (!canWrite) return
    const qty = Number(movQuantity.replace(',', '.'))
    if (!movConsumableId) {
      setErr('Выберите расходник.')
      return
    }
    if (!Number.isFinite(qty) || qty === 0) {
      setErr('Укажите ненулевое количество.')
      return
    }

    setSaving(true)
    setErr('')
    try {
      const signedQty =
        movType === 'adjustment' ? qty : movType === 'issue' ? -Math.abs(qty) : Math.abs(qty)

      const movementBody: Record<string, unknown> = {
        consumable: movConsumableId,
        type: movType,
        quantity: signedQty,
        notes: movNotes.trim() || '',
      }
      if (user?.id) movementBody.createdBy = user.id
      await pb.collection(COL_INVENTORY_MOVEMENTS).create(movementBody)

      const consumable = consumables.find((c) => c.id === movConsumableId)
      if (consumable) {
        const nextStock = Math.max(0, Number(consumable.stockQty ?? 0) + stockDelta(movType, signedQty))
        await pb.collection(COL_CONSUMABLES).update(
          movConsumableId,
          consumableSavePatch(consumable, { stockQty: nextStock }),
        )
      }

      setModalOpen(false)
      await load()
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка сохранения движения'))
    } finally {
      setSaving(false)
    }
  }

  const tabs: { id: TabId; label: string; badge?: number }[] = [
    { id: 'stock', label: 'Остатки сырья' },
    { id: 'fg_lots', label: 'Партии ГП' },
    { id: 'movements', label: 'Движения' },
    { id: 'alerts', label: 'Мин. запас', badge: alerts.length || undefined },
  ]

  return (
    <PageShell>
      <PageHeader
        title="Склад"
        lead="Остатки расходников, партии готовой продукции, журнал движений и предупреждения при падении ниже минимального запаса."
      />

      {err && !modalOpen && <Alert kind="err">{err}</Alert>}

      {alerts.length > 0 && tab !== 'alerts' && (
        <Alert kind="warn">
          {alerts.length}{' '}
          {alerts.length === 1 ? 'позиция ниже' : 'позиций ниже'} минимального запаса.{' '}
          <button type="button" className="ml-1 font-semibold underline" onClick={() => setTab('alerts')}>
            Показать
          </button>
        </Alert>
      )}

      <div className={toolbarClass}>
        {canWrite && (
          <button type="button" className={btn('primary')} onClick={() => openMovementModal()}>
            Движение
          </button>
        )}
        <button type="button" className={btn()} onClick={() => void load()} disabled={loading}>
          Обновить
        </button>
        {tab === 'stock' && filteredStock.length > 0 && (
          <ExportCsvButton filename="inventory-stock" rows={filteredStock} columns={stockCsvColumns} />
        )}
        {tab === 'movements' && filteredMovements.length > 0 && (
          <ExportCsvButton filename="inventory-movements" rows={filteredMovements} columns={movementsCsvColumns} />
        )}
        {tab === 'alerts' && filteredAlerts.length > 0 && (
          <ExportCsvButton filename="inventory-alerts" rows={filteredAlerts} columns={stockCsvColumns} />
        )}
        {tab === 'fg_lots' && filteredFgLots.length > 0 && (
          <ExportCsvButton filename="finished-goods-lots" rows={filteredFgLots} columns={fgLotsCsvColumns} />
        )}
        <div className="flex min-w-40 flex-1 justify-end">
          <SearchField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Поиск…"
            aria-label="Поиск по складу"
          />
        </div>
      </div>

      <div className={tabsWrapClass}>
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={cn(tabLink(tab === t.id), 'border-none bg-transparent cursor-pointer')}
            onClick={() => setTab(t.id)}
          >
            {t.label}
            {t.badge != null && t.badge > 0 && (
              <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 py-0.5 text-xs font-bold text-white">
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className={cn('mt-4', tableWrapClass, tableLastRowClass)}>
        {tab === 'stock' && (
          <table className={tableClass}>
            <thead>
              <tr>
                <th className={tableHeadClass}>Наименование</th>
                <th className={tableHeadClass}>Код</th>
                <th className={tableHeadClass}>Ед.</th>
                <th className={tableHeadClass}>Остаток</th>
                <th className={tableHeadClass}>Мин. запас</th>
                <th className={tableHeadClass} />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr className={tableRowHoverClass}>
                  <td className={tableCellClass} colSpan={6}>
                    <em>Загрузка…</em>
                  </td>
                </tr>
              )}
              {!loading && filteredStock.length === 0 && (
                <tr className={tableRowHoverClass}>
                  <td className={tableCellClass} colSpan={6}>
                    <em>{consumables.length === 0 ? 'Нет расходников' : 'Ничего не найдено'}</em>
                  </td>
                </tr>
              )}
              {!loading &&
                filteredStock.map((row) => {
                  const low = isLowStock(row)
                  return (
                    <tr key={row.id} className={cn(tableRowHoverClass, low && 'bg-amber-50/60 dark:bg-amber-950/20')}>
                      <td className={tableCellClass}>{row.name}</td>
                      <td className={tableCellClass}>{row.code?.trim() || '—'}</td>
                      <td className={tableCellClass}>{unitLabel(row.unit)}</td>
                      <td className={cn(tableCellClass, low && 'font-semibold text-amber-800 dark:text-amber-200')}>
                        {formatQty(Number(row.stockQty ?? 0))}
                      </td>
                      <td className={tableCellClass}>
                        {row.minStock != null && Number(row.minStock) > 0 ? formatQty(Number(row.minStock)) : '—'}
                      </td>
                      <td className={cn(tableCellClass, 'whitespace-nowrap')}>
                        {canWrite && (
                          <button type="button" className={btn('ghost', 'sm')} onClick={() => openMovementModal(row.id)}>
                            Движение
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        )}

        {tab === 'movements' && (
          <table className={tableClass}>
            <thead>
              <tr>
                <th className={tableHeadClass}>Дата</th>
                <th className={tableHeadClass}>Расходник</th>
                <th className={tableHeadClass}>Тип</th>
                <th className={tableHeadClass}>Кол-во</th>
                <th className={tableHeadClass}>Примечание</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr className={tableRowHoverClass}>
                  <td className={tableCellClass} colSpan={5}>
                    <em>Загрузка…</em>
                  </td>
                </tr>
              )}
              {!loading && filteredMovements.length === 0 && (
                <tr className={tableRowHoverClass}>
                  <td className={tableCellClass} colSpan={5}>
                    <em>{movements.length === 0 ? 'Нет движений' : 'Ничего не найдено'}</em>
                  </td>
                </tr>
              )}
              {!loading &&
                filteredMovements.map((row) => (
                  <tr key={row.id} className={tableRowHoverClass}>
                    <td className={tableCellClass}>{formatDateTimeRu(row.created)}</td>
                    <td className={tableCellClass}>{row.expand?.consumable?.name ?? '—'}</td>
                    <td className={tableCellClass}>{MOVEMENT_TYPE_LABELS[row.type] ?? row.type}</td>
                    <td className={tableCellClass}>{formatQty(Number(row.quantity))}</td>
                    <td className={tableCellClass}>{row.notes?.trim() || '—'}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}

        {tab === 'fg_lots' && (
          <table className={tableClass}>
            <thead>
              <tr>
                <th className={tableHeadClass}>Продукция</th>
                <th className={tableHeadClass}>Код партии</th>
                <th className={tableHeadClass}>Кол-во</th>
                <th className={tableHeadClass}>Дата выпуска</th>
                <th className={tableHeadClass}>Слот</th>
                <th className={tableHeadClass}>Ячейка</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr className={tableRowHoverClass}>
                  <td className={tableCellClass} colSpan={6}>
                    <em>Загрузка…</em>
                  </td>
                </tr>
              )}
              {!loading && filteredFgLots.length === 0 && (
                <tr className={tableRowHoverClass}>
                  <td className={tableCellClass} colSpan={6}>
                    <em>{fgLots.length === 0 ? 'Нет партий ГП (создаются при закрытии слота)' : 'Ничего не найдено'}</em>
                  </td>
                </tr>
              )}
              {!loading &&
                filteredFgLots.map((row) => (
                  <tr key={row.id} className={tableRowHoverClass}>
                    <td className={tableCellClass}>{row.expand?.product?.name ?? '—'}</td>
                    <td className={tableCellClass}>{row.lotCode?.trim() || '—'}</td>
                    <td className={tableCellClass}>{formatQty(Number(row.quantity))}</td>
                    <td className={tableCellClass}>{formatDateRu(row.producedAt)}</td>
                    <td className={tableCellClass}>
                      {row.scheduleSlot ? row.scheduleSlot.slice(0, 8) : '—'}
                    </td>
                    <td className={tableCellClass}>{row.warehouseLocation?.trim() || '—'}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}

        {tab === 'alerts' && (
          <table className={tableClass}>
            <thead>
              <tr>
                <th className={tableHeadClass}>Наименование</th>
                <th className={tableHeadClass}>Остаток</th>
                <th className={tableHeadClass}>Мин. запас</th>
                <th className={tableHeadClass}>Дефицит</th>
                <th className={tableHeadClass} />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr className={tableRowHoverClass}>
                  <td className={tableCellClass} colSpan={5}>
                    <em>Загрузка…</em>
                  </td>
                </tr>
              )}
              {!loading && filteredAlerts.length === 0 && (
                <tr className={tableRowHoverClass}>
                  <td className={tableCellClass} colSpan={5}>
                    <em>
                      {alerts.length === 0
                        ? 'Все позиции выше минимального запаса (или порог не задан).'
                        : 'Ничего не найдено'}
                    </em>
                  </td>
                </tr>
              )}
              {!loading &&
                filteredAlerts.map((row) => {
                  const stock = Number(row.stockQty ?? 0)
                  const min = Number(row.minStock ?? 0)
                  const deficit = Math.max(0, min - stock)
                  return (
                    <tr key={row.id} className={cn(tableRowHoverClass, 'bg-amber-50/60 dark:bg-amber-950/20')}>
                      <td className={tableCellClass}>{row.name}</td>
                      <td className={tableCellClass}>{formatQty(stock)}</td>
                      <td className={tableCellClass}>{formatQty(min)}</td>
                      <td className="border-b border-su-border px-4 py-2.5 text-left font-semibold text-amber-800 dark:text-amber-200">
                        −{formatQty(deficit)}
                      </td>
                      <td className={cn(tableCellClass, 'whitespace-nowrap')}>
                        {canWrite && (
                          <button type="button" className={btn('primary', 'sm')} onClick={() => openMovementModal(row.id)}>
                            Приход
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen && (
        <Modal title="Движение по складу" onClose={() => setModalOpen(false)}>
          {err && <Alert kind="err">{err}</Alert>}
          <div className="grid gap-4">
            <FormField label="Расходник" htmlFor="mov-consumable">
              <select
                id="mov-consumable"
                className={inputClass}
                value={movConsumableId}
                onChange={(e) => setMovConsumableId(e.target.value)}
              >
                <option value="">— выберите —</option>
                {consumables.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({formatQty(Number(c.stockQty ?? 0))} {unitLabel(c.unit)})
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Тип" htmlFor="mov-type">
              <select
                id="mov-type"
                className={inputClass}
                value={movType}
                onChange={(e) => setMovType(e.target.value as InventoryMovementType)}
              >
                {MANUAL_MOVEMENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {MOVEMENT_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Количество" htmlFor="mov-qty">
              <input
                id="mov-qty"
                type="number"
                step="any"
                {...formInputProps()}
                value={movQuantity}
                onChange={(e) => setMovQuantity(e.target.value)}
              />
            </FormField>
            {movType === 'adjustment' && (
              <p className="m-0 text-xs text-su-text">Для корректировки можно указать отрицательное значение.</p>
            )}
            <FormField label="Примечание" htmlFor="mov-notes">
              <textarea
                id="mov-notes"
                className={cn(inputClass, 'min-h-16 resize-y')}
                value={movNotes}
                onChange={(e) => setMovNotes(e.target.value)}
              />
            </FormField>
          </div>
          <ModalActions>
            <button type="button" className={modalBtn()} onClick={() => setModalOpen(false)}>
              Отмена
            </button>
            <button type="button" className={modalBtn('primary')} disabled={saving} onClick={() => void saveMovement()}>
              {saving ? 'Сохранение…' : 'Сохранить'}
            </button>
          </ModalActions>
        </Modal>
      )}
    </PageShell>
  )
}
